import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Wallet, Save, RefreshCw, Search, Users, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { PageHeader, Card, Button, Input, Select, Skeleton } from "@/components/ui-kit";
import { formatIDR } from "@/lib/format";

/**
 * Iter v34ay — Master Tarif Overtime.
 * Bulk edit tarif overtime per karyawan (Rp/jam). Editing inline, save bulk.
 */
export default function MasterTarifOvertime() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [department, setDepartment] = useState("");
  const [departments, setDepartments] = useState([]);
  const [edits, setEdits] = useState({}); // {employee_id: newRate}
  const [bulkValue, setBulkValue] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [rates, cats] = await Promise.all([
        api.get("/overtime/rates"),
        api.get("/department-categories").catch(() => ({ data: [] })),
      ]);
      setData(rates.data);
      setDepartments(cats.data || []);
      setEdits({});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat data");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    const s = search.trim().toLowerCase();
    return (data.rows || []).filter((r) => {
      if (department && r.department !== department) return false;
      if (s) {
        const hay = `${r.name} ${r.employee_code} ${r.position} ${r.department}`.toLowerCase();
        if (!hay.includes(s)) return false;
      }
      return true;
    });
  }, [data, search, department]);

  const setRate = (id, v) => {
    setEdits((prev) => ({ ...prev, [id]: v }));
  };
  const applyBulk = () => {
    const v = Number(bulkValue);
    if (!Number.isFinite(v) || v < 0) { toast.error("Nilai tarif tidak valid"); return; }
    const next = { ...edits };
    filtered.forEach((r) => { next[r.employee_id] = String(v); });
    setEdits(next);
    toast.success(`Set tarif ${formatIDR(v)} ke ${filtered.length} karyawan (belum disimpan)`);
  };

  const dirtyCount = Object.keys(edits).length;

  const save = async () => {
    if (!dirtyCount) { toast.error("Tidak ada perubahan untuk disimpan"); return; }
    setSaving(true);
    try {
      const items = Object.entries(edits).map(([employee_id, rate]) => ({
        employee_id, rate: Math.max(0, Number(rate) || 0),
      }));
      const r = await api.post("/overtime/rates/bulk", { items });
      toast.success(`Berhasil update ${r.data?.updated || 0} karyawan`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan");
    } finally { setSaving(false); }
  };

  const getRate = (row) => {
    const edit = edits[row.employee_id];
    if (edit !== undefined) return edit;
    return row.overtime_hourly_rate || 0;
  };

  return (
    <div className="min-h-full bg-paper p-4 md:p-6 lg:p-8">
      <div className="max-w-[1400px] mx-auto space-y-6">
        <PageHeader
          overline="HR · Master Data"
          title="Master Tarif Overtime"
          description="Kelola tarif upah lembur per jam (Rp) untuk setiap karyawan. Kosongkan (0) untuk pakai tarif default global."
          action={
            <div className="flex items-center gap-2">
              <Button onClick={load} variant="secondary" data-testid="tarif-refresh"><RefreshCw className="h-3.5 w-3.5"/> Reload</Button>
              <Button onClick={save} disabled={!dirtyCount || saving} data-testid="tarif-save">
                <Save className="h-3.5 w-3.5"/> Simpan {dirtyCount > 0 ? `(${dirtyCount})` : ""}
              </Button>
            </div>
          }
        />

        {/* Default rate info */}
        <div className="rounded-2xl bg-gradient-to-r from-blue-50 to-emerald-50 border border-blue-100 p-4 flex items-center gap-3" data-testid="tarif-default-info">
          <Sparkles className="w-5 h-5 text-blue-700 shrink-0"/>
          <div className="flex-1 text-sm">
            <b className="text-blue-800">Tarif Default Global: {formatIDR(data?.default_rate ?? 0)}</b>
            <span className="text-stone-600 ml-2">— karyawan dengan tarif 0 / kosong akan pakai tarif default ini. Ubah default di <b>Pengaturan → Data Perusahaan</b>.</span>
          </div>
        </div>

        {/* Filter + bulk edit */}
        <Card className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
            <div className="md:col-span-2 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-stone-400"/>
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-stone-600 mb-1.5">Cari</label>
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Nama, kode, posisi…" className="!pl-9" data-testid="tarif-search"/>
            </div>
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-stone-600 mb-1.5">Departemen</label>
              <Select value={department} onChange={(e) => setDepartment(e.target.value)} data-testid="tarif-department">
                <option value="">Semua Departemen</option>
                {departments.map((d) => <option key={d.id || d.name} value={d.name}>{d.name}</option>)}
              </Select>
            </div>
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-stone-600 mb-1.5">Bulk Set (yg tampil)</label>
              <Input type="number" min="0" step="1000" value={bulkValue} onChange={(e) => setBulkValue(e.target.value)} placeholder="mis: 25000" data-testid="tarif-bulk-value"/>
            </div>
            <div>
              <Button onClick={applyBulk} variant="secondary" disabled={!bulkValue || !filtered.length} data-testid="tarif-bulk-apply">
                <Users className="h-3.5 w-3.5"/> Terapkan ke {filtered.length}
              </Button>
            </div>
          </div>
        </Card>

        {/* Table */}
        <Card className="p-0">
          {loading ? (
            <div className="p-4 space-y-2">
              {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-12 w-full"/>)}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 text-stone-400" data-testid="tarif-empty">
              Tidak ada karyawan yang cocok dengan filter.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="tarif-table">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-widest text-stone-500 border-b border-stone-200 bg-stone-50">
                    <th className="py-3 px-3">#</th>
                    <th className="py-3 px-3">Kode</th>
                    <th className="py-3 px-3">Nama Karyawan</th>
                    <th className="py-3 px-3">Departemen</th>
                    <th className="py-3 px-3">Posisi</th>
                    <th className="py-3 px-3 text-right">Tarif Saat Ini</th>
                    <th className="py-3 px-3 text-right">Tarif Baru (Rp/jam)</th>
                    <th className="py-3 px-3 text-right">Effective</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r, i) => {
                    const editing = edits[r.employee_id] !== undefined;
                    const currentVal = getRate(r);
                    const numVal = Number(currentVal) || 0;
                    const effective = numVal > 0 ? numVal : (data?.default_rate || 0);
                    return (
                      <tr key={r.employee_id} className={`border-b border-stone-100 hover:bg-stone-50/50 ${editing ? "bg-amber-50/30" : ""}`} data-testid={`tarif-row-${r.employee_id}`}>
                        <td className="py-2 px-3 text-stone-500">{i + 1}</td>
                        <td className="py-2 px-3 font-mono-num text-xs text-stone-600">{r.employee_code || "-"}</td>
                        <td className="py-2 px-3 font-medium text-stone-900">{r.name}</td>
                        <td className="py-2 px-3 text-stone-600">{r.department || "-"}</td>
                        <td className="py-2 px-3 text-stone-600">{r.position || "-"}</td>
                        <td className="py-2 px-3 text-right font-mono-num text-stone-500">
                          {r.overtime_hourly_rate > 0 ? formatIDR(r.overtime_hourly_rate) : <span className="text-stone-400 italic">default</span>}
                        </td>
                        <td className="py-2 px-3 text-right">
                          <input
                            type="number"
                            min="0"
                            step="1000"
                            value={currentVal}
                            onChange={(e) => setRate(r.employee_id, e.target.value)}
                            className="w-32 text-right border border-stone-300 rounded-lg px-2 py-1.5 text-sm font-mono-num focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                            data-testid={`tarif-input-${r.employee_id}`}
                          />
                        </td>
                        <td className="py-2 px-3 text-right font-mono-num font-medium text-blue-800">
                          {formatIDR(effective)}
                          {numVal <= 0 && <span className="ml-1 text-[9px] uppercase tracking-widest text-stone-400">default</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
